(function () {
  class ProductUgcVideos {
    constructor(section) {
      this.section = section;
      this.carousel = section.querySelector('.product-ugc-videos__carousel');
      this.track = section.querySelector('[data-ugc-track]');
      this.dots = section.querySelector('[data-ugc-dots]');
      this.cards = Array.from(section.querySelectorAll('[data-ugc-card]'));
      this.videos = Array.from(section.querySelectorAll('[data-ugc-preview]'));
      this.modal = section.querySelector('[data-ugc-modal]');
      this.modalVideo = section.querySelector('[data-ugc-modal-video]');
      this.closeButton = section.querySelector('[data-ugc-close]');
      this.pagePositions = [];
      this.activePage = 0;
      this.previewIndex = 0;
      this.previewTimer = null;
      this.previewCleanup = null;
      this.isAutoScrolling = false;
      this.isInView = false;
      this.isModalOpen = false;
      this.userPausedUntil = 0;
      this.previewStart = Math.max(0, Number(section.dataset.ugcPreviewStart) || 0);
      this.previewDuration = 3;

      if (!this.track || !this.cards.length) return;

      this.bindEvents();
      this.createObserver();
      this.updateCarousel();
      this.preparePreviewFrames();
      this.queuePreview();
    }

    bindEvents() {
      this.track.addEventListener('scroll', () => {
        window.requestAnimationFrame(() => this.updateActiveDot());
        if (this.isAutoScrolling) return;
        this.pauseForUserScroll(1500);
      });
      ['pointerdown', 'touchstart', 'wheel'].forEach((eventName) => {
        this.track.addEventListener(eventName, () => this.pauseForUserScroll(2800), { passive: true });
      });

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.openModal(card));
        card.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.openModal(card);
        });
      });

      if (this.closeButton) this.closeButton.addEventListener('click', () => this.closeModal());
      if (this.modal) {
        this.modal.addEventListener('click', (event) => {
          if (event.target === this.modal) this.closeModal();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isModalOpen) this.closeModal();
      });

      window.addEventListener('resize', this.debounce(() => {
        this.updateCarousel();
        this.queuePreview();
      }, 150));
    }

    createObserver() {
      if (!('IntersectionObserver' in window)) {
        this.isInView = true;
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isInView = entry.isIntersecting;
          if (this.isInView) this.queuePreview();
          else this.pauseAllPreviews(true);
        });
      }, { threshold: 0.35 });

      this.observer.observe(this.section);
    }

    debounce(callback, delay) {
      let timer = null;
      return () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(callback, delay);
      };
    }

    updateCarousel() {
      if (!this.dots) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (maxScroll <= 2) {
        this.carousel?.classList.remove('is-overflowing');
        this.pagePositions = [0];
        this.dots.hidden = true;
        this.dots.innerHTML = '';
        return;
      }

      this.carousel?.classList.add('is-overflowing');
      const pageSize = Math.max(1, this.track.clientWidth);
      const pageCount = Math.max(2, Math.ceil(maxScroll / pageSize) + 1);
      this.pagePositions = Array.from({ length: pageCount }, (_, index) => {
        if (index === pageCount - 1) return maxScroll;
        return Math.min(maxScroll, Math.round(index * pageSize));
      });

      this.dots.hidden = false;
      this.dots.innerHTML = this.pagePositions
        .map((_, index) => (
          `<button type="button" class="product-ugc-videos__dot${index === this.activePage ? ' is-active' : ''}" data-ugc-dot="${index}" aria-label="Go to video page ${index + 1}" aria-selected="${index === this.activePage ? 'true' : 'false'}"></button>`
        ))
        .join('');

      this.dots.querySelectorAll('[data-ugc-dot]').forEach((dot) => {
        dot.addEventListener('click', () => this.scrollToPage(Number(dot.dataset.ugcDot) || 0));
      });

      this.updateActiveDot();
    }

    scrollToPage(index) {
      const safeIndex = Math.max(0, Math.min(index, this.pagePositions.length - 1));
      this.track.scrollTo({ left: this.pagePositions[safeIndex] || 0, behavior: 'smooth' });
      this.setActiveDot(safeIndex);
    }

    updateActiveDot() {
      if (!this.pagePositions.length) return;

      const current = this.track.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Infinity;
      this.pagePositions.forEach((position, index) => {
        const distance = Math.abs(position - current);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      this.setActiveDot(nearestIndex);
    }

    setActiveDot(index) {
      this.activePage = index;
      if (!this.dots) return;

      this.dots.querySelectorAll('[data-ugc-dot]').forEach((dot) => {
        const isActive = Number(dot.dataset.ugcDot) === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    getVisibleVideos() {
      const trackRect = this.track.getBoundingClientRect();
      return this.videos.filter((video) => {
        const rect = video.closest('[data-ugc-card]').getBoundingClientRect();
        return rect.right > trackRect.left + 8 && rect.left < trackRect.right - 8;
      });
    }

    syncPreviewIndexToVisible() {
      const visibleVideos = this.getVisibleVideos();
      if (!visibleVideos.length) return;

      const nextVisibleIndex = this.videos.indexOf(visibleVideos[0]);
      if (nextVisibleIndex >= 0) this.previewIndex = nextVisibleIndex;
    }

    previewStartTime(video) {
      const duration = Number(video?.duration);
      if (!Number.isFinite(duration) || duration <= 0) return this.previewStart;
      return Math.min(this.previewStart, Math.max(0, duration - 0.1));
    }

    setPreviewFrame(video) {
      if (!video) return;

      const target = this.previewStartTime(video);
      try {
        if (Math.abs(video.currentTime - target) > 0.05) video.currentTime = target;
      } catch (error) {
        // Some browsers reject seeks before metadata is ready; loadedmetadata retries below.
      }
    }

    preparePreviewFrames() {
      if (!this.previewStart) return;

      this.videos.forEach((video) => {
        const setFrame = () => this.setPreviewFrame(video);
        if (video.readyState >= 1) {
          setFrame();
        } else {
          video.addEventListener('loadedmetadata', setFrame, { once: true });
          video.load();
        }
      });
    }

    isVideoVisible(video) {
      if (!video) return false;

      const trackRect = this.track.getBoundingClientRect();
      const card = video.closest('[data-ugc-card]');
      if (!card) return false;

      const rect = card.getBoundingClientRect();
      return rect.left >= trackRect.left - 2 && rect.right <= trackRect.right + 2;
    }

    scrollVideoIntoView(video) {
      const card = video && video.closest('[data-ugc-card]');
      if (!card) return;

      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const target = Math.max(0, Math.min(maxScroll, card.offsetLeft - this.track.offsetLeft));
      this.isAutoScrolling = true;
      this.track.scrollTo({ left: target, behavior: 'smooth' });
      window.setTimeout(() => {
        this.isAutoScrolling = false;
        this.updateActiveDot();
      }, 460);
    }

    queuePreview(delay = 120) {
      window.clearTimeout(this.previewTimer);
      const waitForUserPause = Math.max(0, this.userPausedUntil - Date.now());
      this.previewTimer = window.setTimeout(() => this.playNextPreview(), Math.max(delay, waitForUserPause));
    }

    pauseForUserScroll(delay = 1500) {
      this.userPausedUntil = Date.now() + delay;
      this.syncPreviewIndexToVisible();
      this.pauseAllPreviews(false);
      this.queuePreview(delay);
    }

    playNextPreview() {
      if (!this.isInView || this.isModalOpen) return;
      if (Date.now() < this.userPausedUntil) {
        this.queuePreview(this.userPausedUntil - Date.now());
        return;
      }

      if (!this.videos.length) {
        this.pauseAllPreviews(true);
        return;
      }

      this.previewIndex = this.previewIndex % this.videos.length;
      const video = this.videos[this.previewIndex];
      if (!this.isVideoVisible(video)) {
        this.scrollVideoIntoView(video);
        this.previewTimer = window.setTimeout(() => this.startPreview(video), 520);
        return;
      }

      this.startPreview(video);
    }

    startPreview(video) {
      if (!this.isInView || this.isModalOpen || !video) return;

      if (video.readyState < 1) {
        video.addEventListener('loadedmetadata', () => this.startPreview(video), { once: true });
        video.load();
        return;
      }

      if (this.previewCleanup) this.previewCleanup();
      this.pauseAllPreviews(false, video);
      video.muted = true;
      video.controls = false;
      const startAt = this.previewStartTime(video);
      this.setPreviewFrame(video);

      let didFinish = false;
      const finishPreview = () => {
        if (didFinish) return;
        didFinish = true;
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('ended', finishPreview);
        video.pause();
        this.setPreviewFrame(video);
        this.previewCleanup = null;
        this.previewIndex = (this.previewIndex + 1) % this.videos.length;
        this.queuePreview(250);
      };

      const onTimeUpdate = () => {
        if (video.currentTime >= startAt + this.previewDuration) finishPreview();
      };

      this.previewCleanup = () => {
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('ended', finishPreview);
        this.previewCleanup = null;
      };
      video.addEventListener('timeupdate', onTimeUpdate);
      video.addEventListener('ended', finishPreview);
      video.play().catch(() => {
        if (this.previewCleanup) this.previewCleanup();
        this.setPreviewFrame(video);
      });
    }

    pauseAllPreviews(reset, exceptVideo) {
      window.clearTimeout(this.previewTimer);
      if (this.previewCleanup) this.previewCleanup();
      this.videos.forEach((video) => {
        if (video === exceptVideo) return;
        video.pause();
        if (reset) this.setPreviewFrame(video);
      });
    }

    openModal(card) {
      const preview = card.querySelector('[data-ugc-preview]');
      if (!preview || !this.modal || !this.modalVideo) return;

      const source = preview.currentSrc || preview.querySelector('source')?.src || preview.src;
      if (!source) return;

      this.isModalOpen = true;
      this.pauseAllPreviews(false);
      this.modalVideo.src = source;
      this.modalVideo.poster = preview.getAttribute('data-native-poster') || preview.getAttribute('poster') || '';
      this.modalVideo.currentTime = 0;
      this.modalVideo.muted = false;
      this.modalVideo.controls = true;
      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-ugc-videos-modal-open');
      this.closeButton?.focus();
      this.modalVideo.play().catch(() => {});
    }

    closeModal() {
      if (!this.modal || !this.modalVideo) return;

      this.isModalOpen = false;
      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.modalVideo.pause();
      this.modalVideo.removeAttribute('src');
      this.modalVideo.load();
      document.body.classList.remove('product-ugc-videos-modal-open');
      this.queuePreview(250);
    }
  }

  function initProductUgcVideos(scope) {
    (scope || document).querySelectorAll('[data-product-ugc-videos]').forEach((section) => {
      if (section.dataset.ugcVideosReady === 'true') return;
      section.dataset.ugcVideosReady = 'true';
      new ProductUgcVideos(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initProductUgcVideos(document));
  document.addEventListener('shopify:section:load', (event) => initProductUgcVideos(event.target));
})();
